import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Divider, Typography } from '@mui/material';
import PrintIcon from '@material-ui/icons/Print';
import { PackBox, PackOrder, PackSession } from '../../../services/fulfillment/types';

interface Props {
  order: PackOrder;
  session: PackSession;
  onSessionChange: (s: PackSession) => void;
  onNext: () => void;
  onBack: () => void;
}

const buildSlipHtml = (order: PackOrder, session: PackSession, box: PackBox, idx: number) => {
  const addr = order.shippingAddress;
  const itemRows = order.lines
    .map(line => {
      const ls = session.lineStates[line.shopifyLineItemGid];
      const inBox = ls?.boxId === box.id;
      const exc = ls?.exceptionType;
      const rowClass = exc ? 'exc-row' : inBox ? 'in-box' : 'other-row';
      const indicator = exc ? '&#9888; EXCEPTION' : inBox ? '&#9656; IN THIS BOX' : '&nbsp;';
      return `<tr class="${rowClass}">
        <td class="sku">${line.sku}</td>
        <td>${line.title}</td>
        <td class="weight">${line.weightLbs} lbs</td>
        <td class="indicator">${indicator}</td>
      </tr>`;
    })
    .join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Packing Slip &mdash; ${order.shopifyOrderName} Box ${idx + 1}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; margin: 0; padding: 32px; color: #111; max-width: 760px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 20px; border-bottom: 3px solid #111; margin-bottom: 24px; }
    .logo-text { font-size: 24px; font-weight: 900; letter-spacing: -0.5px; color: #111; }
    .logo-text em { font-style: normal; color: #e53e3e; }
    .logo-sub { font-size: 11px; color: #888; margin-top: 3px; }
    .order-info { text-align: right; }
    .order-name { font-size: 28px; font-weight: 900; }
    .order-date { font-size: 12px; color: #666; margin-top: 4px; }
    .box-badge { display: inline-block; margin-top: 8px; background: #1a56db; color: #fff; border-radius: 4px; padding: 5px 14px; font-size: 13px; font-weight: 700; }
    .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 28px; }
    .section-label { font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #888; margin-bottom: 6px; }
    .addr { font-size: 14px; line-height: 1.7; }
    .addr strong { font-size: 15px; display: block; margin-bottom: 2px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    thead { background: #f3f4f6; }
    th { text-align: left; padding: 8px 10px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: #666; font-weight: 700; }
    td { padding: 10px 10px; border-bottom: 1px solid #e5e7eb; vertical-align: middle; }
    .sku { font-weight: 700; font-family: monospace; font-size: 12px; white-space: nowrap; }
    .weight { color: #666; white-space: nowrap; }
    .indicator { white-space: nowrap; font-size: 11px; font-weight: 700; }
    .in-box { background: #f0fdf4; }
    .in-box .indicator { color: #16a34a; }
    .other-row td { color: #aaa; }
    .other-row .sku { font-weight: 400; }
    .exc-row { background: #fef2f2; }
    .exc-row .indicator { color: #dc2626; }
    .footer { margin-top: 28px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #aaa; }
    .print-controls { margin-top: 32px; padding-top: 20px; border-top: 2px dashed #e5e7eb; }
    .print-btn { padding: 10px 28px; background: #1a56db; color: #fff; border: none; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer; margin-right: 12px; }
    .close-btn { padding: 10px 24px; background: #fff; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; cursor: pointer; }
    @media print { .print-controls { display: none !important; } body { padding: 16px; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="logo-text">The Pros<em>Closet</em></div>
      <div class="logo-sub">theproscloset.com</div>
    </div>
    <div class="order-info">
      <div class="order-name">${order.shopifyOrderName}</div>
      <div class="order-date">${new Date(order.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })}</div>
      <div><span class="box-badge">Box ${idx + 1} of ${session.boxes.length} &mdash; ${
    box.size
  }</span></div>
    </div>
  </div>

  <div class="two-col">
    <div>
      <div class="section-label">Customer</div>
      <div class="addr">
        <strong>${order.customer.firstName} ${order.customer.lastName}</strong>
        ${order.customer.email}
      </div>
    </div>
    <div>
      <div class="section-label">Ship To</div>
      <div class="addr">
        <strong>${order.customer.firstName} ${order.customer.lastName}</strong>
        ${addr.address1}${addr.address2 ? '<br>' + addr.address2 : ''}<br>
        ${addr.city}, ${addr.province} ${addr.zip}<br>
        ${addr.country}
      </div>
    </div>
  </div>

  <div class="section-label">Items</div>
  <table>
    <thead>
      <tr><th>SKU</th><th>Description</th><th>Weight</th><th>Status</th></tr>
    </thead>
    <tbody>${itemRows}</tbody>
  </table>

  <div class="footer">
    Order ID: ${
      order.shopifyOrderId
    } &nbsp;&middot;&nbsp; Packing slip generated ${new Date().toLocaleString('en-US')}
  </div>

  <div class="print-controls">
    <button class="print-btn" onclick="window.print()">Print Slip</button>
    <button class="close-btn" onclick="window.close()">Close</button>
  </div>
</body>
</html>`;
};

const MockPackSlipsStep: React.FC<Props> = ({
  order,
  session,
  onSessionChange,
  onNext,
  onBack,
}) => {
  const [advanceWarning, setAdvanceWarning] = useState(false);

  const unprinted = session.boxes.filter(b => !session.slipsPrinted[b.id]);

  const handlePrint = (box: PackBox, idx: number) => {
    const win = window.open('', '_blank', 'width=800,height=950');
    if (!win) return;
    win.document.write(buildSlipHtml(order, session, box, idx));
    win.document.close();
    onSessionChange({ ...session, slipsPrinted: { ...session.slipsPrinted, [box.id]: true } });
  };

  const handleNext = () => {
    if (unprinted.length > 0) {
      setAdvanceWarning(true);
      return;
    }
    onNext();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Print a slip for each box before sealing.
        </Typography>
        <Button size="small" variant="text" onClick={onBack}>
          &#8592; Scan Items
        </Button>
      </Box>

      {advanceWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAdvanceWarning(false)}>
          {unprinted.length} slip{unprinted.length !== 1 ? 's' : ''} not yet printed. You can still
          advance, but packing slips are required for each box.
        </Alert>
      )}

      {session.boxes.map((box, idx) => {
        const printed = session.slipsPrinted[box.id];

        return (
          <Box
            key={box.id}
            sx={{
              mb: 2,
              border: '1px solid',
              borderColor: printed ? 'success.light' : 'grey.300',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                bgcolor: printed ? '#f0faf4' : 'grey.50',
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700 }}>
                  Box {idx + 1} of {session.boxes.length}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'grey.600' }}>{box.size}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {printed && <Chip label="Printed" size="small" color="success" />}
                <Button
                  size="small"
                  variant={printed ? 'outlined' : 'contained'}
                  startIcon={<PrintIcon />}
                  onClick={() => handlePrint(box, idx)}
                >
                  {printed ? 'Reprint' : 'Print Slip'}
                </Button>
              </Box>
            </Box>

            {/* Slip preview */}
            <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'grey.200' }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'grey.500', mb: 1 }}>
                SLIP PREVIEW &mdash; {order.shopifyOrderName} &middot; Box {idx + 1} of{' '}
                {session.boxes.length}
              </Typography>
              {order.lines.map(line => {
                const inThisBox = session.lineStates[line.shopifyLineItemGid]?.boxId === box.id;
                return (
                  <Typography
                    key={line.shopifyLineItemGid}
                    sx={{
                      fontSize: 13,
                      py: 0.25,
                      fontWeight: inThisBox ? 700 : 400,
                      color: inThisBox ? 'text.primary' : 'grey.400',
                    }}
                  >
                    {inThisBox ? '▸ ' : '  '}
                    {line.sku} — {line.title}
                  </Typography>
                );
              })}
              <Divider sx={{ my: 1 }} />
              <Typography sx={{ fontSize: 11, color: 'grey.500' }}>
                Order ID: {order.shopifyOrderId}
              </Typography>
            </Box>
          </Box>
        );
      })}

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button variant="contained" onClick={handleNext}>
          Continue to Labels
        </Button>
      </Box>
    </Box>
  );
};

export default MockPackSlipsStep;
